import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Card, Tag, Text, TextButton } from '@zillow/constellation-10';
import EsusuWrapper from './EsusuWrapper';
import type { InitOptions } from '../../types/esusuSdk';
import { TAG_APPEARANCE_MAP } from '../../constants';
import { CreditClimbCardSkeleton } from '../Skeletons';
import type { YourRentalCreditClimbCardDto } from '@zg-rentals/ts-your-rental-api-client';
import CtaButton from '../shared/CtaButton';

const CONTENT = {
  title: 'CreditClimb',
  enrolledTag: 'Enrolled in rent reporting',
  goToCreditClimb: 'Go to CreditClimb',
  errorMessage: 'Your credit score couldn’t be loaded',
  tryAgain: 'Try again',
} as const;

const CreditClimbErrorCard = ({ onTryAgain }: { onTryAgain: () => void }) => {
  return (
    <Card
      elevated={false}
      css={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: 'loose',
        gap: 'default',
      }}
    >
      <Text textStyle="body-sm-bold" data-testid="RentReporting-PaymentCardTitle">
        {CONTENT.title}
      </Text>
      <Alert
        actionButton={<TextButton onClick={onTryAgain}>{CONTENT.tryAgain}</TextButton>}
        tone="critical"
        css={{ width: '100%' }}
      >
        {CONTENT.errorMessage}
      </Alert>
    </Card>
  );
};

export default function CreditClimbCard({ creditClimbCard }: { creditClimbCard: YourRentalCreditClimbCardDto }) {
  const { cardTitle, cta, statusTag } = creditClimbCard;
  const [hasError, setHasError] = useState(false);
  const [isAppLoaded, setIsAppLoaded] = useState(false);
  const loadTimerRef = useRef<NodeJS.Timeout | null>(null);

  const isExtraSmallScreen = useMemo(() => (typeof window !== 'undefined' ? window.innerWidth <= 420 : false), []);
  const isSmallScreen = useMemo(() => (typeof window !== 'undefined' ? window.innerWidth < 1024 : false), []);

  const creditScoreStyleConfig = {
    container: {
      margin: 0,
      padding: 0,
    },
    title: {
      fontSize: '14px',
      fontWeight: '700',
      lineHeight: '24px',
      letterSpacing: '0.12px',
      marginBottom: 'tighter',
      color: '#000000',
      fontStyle: 'normal',
      textAlign: 'left',
    },
    creditScoreContainer: {
      margin: 0,
      padding: 0,
      fontSize: '44px',
      fontWeight: '800',
    },
    creditScoreChangeContainer: {
      margin: 0,
      paddingLeft: 0,
      paddingRight: 0,
      paddingBottom: 0,
      fontSize: '36px',
      fontWeight: '800',
    },
    disclaimerText: {
      fontSize: '10px',
      textAlign: 'left',
    },
    pointChangeLabel: {
      fontSize: '12px',
      fontWeight: '400',
    },
  };

  // Desktop styles - add left alignment to base styles
  const creditScoreStyleConfigDesktop = {
    ...creditScoreStyleConfig,
    creditScoreChangeContainer: {
      ...creditScoreStyleConfig.creditScoreChangeContainer,
      alignItems: 'flex-start',
    },
    pointChangeLabel: {
      ...creditScoreStyleConfig.pointChangeLabel,
      textAlign: 'left',
    },
  };

  const creditScoreConfig: InitOptions = {
    containerId: 'creditScore',
    view: 'creditScore',
    iframeStyle: {
      minHeight: isExtraSmallScreen ? '300px' : '200px',
      height: 'auto',
    },
    config: {
      text: {
        'widgets.creditScore.title': cardTitle,
      },
      components: {
        global: {
          type: 'global',
          initialLoadingIndicator: {
            style: {
              primaryColor: '#FFFFFF',
            },
          },
        },
        widgets: [
          {
            type: 'widget',
            id: 'creditScore',
            style: isExtraSmallScreen ? creditScoreStyleConfig : creditScoreStyleConfigDesktop,
          },
        ],
      },
    },
    onErrorEvent: () => {
      setHasError(true);
      setIsAppLoaded(true);
    },
    onActionEvent: (event) => {
      if (event.name === 'app_loaded') {
        loadTimerRef.current = setTimeout(() => {
          setIsAppLoaded(true);
        }, 4000);
      }
    },
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (loadTimerRef.current) {
        clearTimeout(loadTimerRef.current);
      }
    };
  }, []);

  if (hasError) {
    return (
      <CreditClimbErrorCard
        onTryAgain={() => {
          setHasError(false);
          setIsAppLoaded(false);
        }}
      />
    );
  }

  return (
    <>
      {/* Always render EsusuWrapper to trigger events, but hide it when loading */}
      <Box css={{ display: isAppLoaded ? 'block' : 'none' }}>
        <Card
          elevated={false}
          css={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: 'loose',
            position: 'relative',
          }}
          data-testid="CreditClimbCard"
        >
          <Tag
            tone={TAG_APPEARANCE_MAP[statusTag.tagAppearance]}
            size={isSmallScreen ? 'sm' : undefined}
            data-testid="CreditClimbCard-EnrolledTag"
            css={{
              position: 'absolute',
              top: isExtraSmallScreen ? '1.6rem' : '24px',
              right: isExtraSmallScreen ? '1.5rem' : '24px',
            }}
          >
            {statusTag.tagText}
          </Tag>
          <EsusuWrapper config={creditScoreConfig} />
          <Box css={{ display: 'flex', justifyContent: 'flex-start' }}>
            <CtaButton cta={cta} dataTestId={`CreditClimbCard-Cta`} />
          </Box>
        </Card>
      </Box>

      {/* Show skeleton while loading */}
      {!isAppLoaded && <CreditClimbCardSkeleton isExtraSmallScreen={isExtraSmallScreen} />}
    </>
  );
}
